import React from 'react';
import styled from 'styled-components';
import { ProjectAssignment } from '../types';

interface ProjectAssignmentBlockProps {
  project: ProjectAssignment;
  onDelete: (project: ProjectAssignment) => void;
  onEdit: (project: ProjectAssignment) => void;
  onCheck: (project: ProjectAssignment) => void;
  onSettings?: () => void;
  onCopy: (project: ProjectAssignment) => void;
  onMenuToggle: (projectId: string) => void;
  isMenuOpen: boolean;
  members: any[];
  currentClassId: string;
  color?: string; // 프로젝트 색상 추가
  selectionMode?: boolean;
  isSelected?: boolean;
  onSelectToggle?: () => void;
}

const ProjectAssignmentBlock: React.FC<ProjectAssignmentBlockProps> = ({ 
  project, 
  onDelete, 
  onEdit, 
  onCheck, 
  onSettings, 
  onCopy,
  onMenuToggle,
  isMenuOpen,
  members, 
  currentClassId,
  color,
  selectionMode = false,
  isSelected = false,
  onSelectToggle
}) => {
  
  // 하위 과제 개수 계산
  const subTaskCount = project.subTasks.length;
  
  // 총 포인트 계산
  const totalPoints = project.subTasks.reduce((sum, subTask) => sum + subTask.points, 0);
  
  // 마감일 포맷팅
  const due = new Date(project.dueDate);
  const weekday = ['일','월','화','수','목','금','토'][due.getDay()];

  const handleClick = () => {
    if (selectionMode) {
      onSelectToggle?.();
      return;
    }
    onCheck(project);
  };

  return (
    <Block 
      onClick={handleClick}
      role="button" 
      tabIndex={0}
      data-project-id={project.id}
      $selectionMode={selectionMode}
      $isSelected={isSelected}
    >
      {selectionMode && (
        <SelectionIndicator $checked={isSelected} aria-label={isSelected ? '선택됨' : '선택 안 됨'}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </SelectionIndicator>
      )}
      {/* 1행: 카테고리, 제목, 햄버거 버튼 */}
      <TopRow>
        <Left>
          <CategoryBadge color={color || project.color || '#FC5555'}>프로젝트</CategoryBadge>
        </Left>
        <TitleCenter>{project.title}</TitleCenter>
        <Right>
          <HamburgerButton
            onClick={(e) => {
              e.stopPropagation();
              if (!selectionMode) {
              onMenuToggle(project.id);
              }
            }}
            aria-label="메뉴"
            className="hamburger-button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="1" fill="currentColor"/>
              <circle cx="19" cy="12" r="1" fill="currentColor"/>
              <circle cx="5" cy="12" r="1" fill="currentColor"/>
            </svg>
          </HamburgerButton>
        </Right>
      </TopRow>
      
      {/* 2행: 마감일 / 전체 포인트 / 완료 보상 */}
      <DueDate>
        <DueDateText>
          {`${due.getMonth()+1}/${due.getDate()} (${weekday}) • 전체포인트 ${totalPoints}`}
          {(project.rewardEnabled && project.rewardCoins !== undefined && project.rewardCoins >= 1) ||
           (project.rewardItemCardIds && project.rewardItemCardIds.length > 0) ? (
            <RewardInfo>
              {project.rewardEnabled && project.rewardCoins !== undefined && project.rewardCoins >= 1 && (
                <RewardItem>
                  <CoinIcon>
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="10" cy="10" r="8" fill="#FFD700" stroke="#FFA500" strokeWidth="1.5"/>
                      <circle cx="10" cy="10" r="5" fill="#FFA500" opacity="0.3"/>
                      <text x="10" y="13" fontSize="8" fontWeight="bold" fill="#FFA500" textAnchor="middle">₩</text>
                    </svg>
                  </CoinIcon>
                  <RewardText>{project.rewardCoins}</RewardText>
                </RewardItem>
              )}
              {project.rewardItemCardIds && project.rewardItemCardIds.length > 0 && (
                <RewardItem>
                  <ItemCardIcon>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                      <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                      <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                    </svg>
                  </ItemCardIcon>
                  <RewardText>{project.rewardItemCardIds.length}</RewardText>
                </RewardItem>
              )}
            </RewardInfo>
          ) : null}
        </DueDateText>
      </DueDate>
      
      {/* 3행: 하위과제 개수 및 제출 확인 */}
      <StatusRow>
        {project.submittedCount !== undefined && project.submittedCount > 0 && (
          <SubmittedInfo>
            제출 확인: {project.submittedCount}명
          </SubmittedInfo>
        )}
        <SubTaskInfo color={color || project.color || '#FC5555'}>
          하위과제: {subTaskCount}개
        </SubTaskInfo>
      </StatusRow>
    </Block>
  );
};

export default ProjectAssignmentBlock;

// ===== 스타일드 컴포넌트 =====

const Block = styled.div<{ $selectionMode?: boolean; $isSelected?: boolean }>`
  background: var(--color-surface);
  border: 2px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  position: relative;
  border-radius: 12px;
  padding: 6px 10px;
  margin: 1px 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  user-select: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: none;
  }
  
  /* 다크 모드에서 선택된 블럭의 테두리를 흰색으로 */
  :root:not([data-theme='light']) & {
    border-color: ${props => props.$isSelected ? '#ffffff' : 'var(--color-border)'};
  }
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 40px;
  align-items: center;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const TitleCenter = styled.div`
  text-align: center;
  font-weight: 800;
  font-size: 1.2rem;
  color: var(--color-text-primary);
  flex: 1;
  opacity: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Right = styled.div`
  display: flex;
  gap: 8px;
  min-width: 0;
  justify-content: flex-end;
`;

const CategoryBadge = styled.span<{ color?: string }>`
  background: ${props => props.color || '#4f46e5'};
  color: white;
  padding: 2px 5px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  max-width: 120px;
  overflow: hidden;
`;

const HamburgerButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--color-text-gray);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const DueDate = styled.div`
  text-align: center;
  color: var(--color-text-gray);
  margin-top: 1px;
  opacity: 1;
`;

const DueDateText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const RewardInfo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 4px;
`;

const RewardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const CoinIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const ItemCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const RewardText = styled.span`
  font-size: inherit;
  font-weight: 600;
  color: inherit;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
`;

const SubTaskInfo = styled.span<{ color?: string }>`
  text-align: center;
  color: ${props => props.color || '#4f46e5'};
  opacity: 1;
`;

const SubmittedInfo = styled.span`
  background: #000000;
  color: #ffffff;
  font-weight: 500;
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 20px;
  margin-right: 12px;
  display: inline-block;
  line-height: 1.2;
`;

const SelectionIndicator = styled.div<{ $checked: boolean }>`
  position: absolute;
  top: -8px;
  left: -8px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${props => props.$checked ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  background: ${props => props.$checked ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$checked ? '#ffffff' : 'var(--color-text-gray)'};
  box-shadow: 0 2px 6px rgba(0,0,0,0.12);
  opacity: 1;
  z-index: 10;
  
  /* 다크 모드에서 선택된 원형 v 도형: 흰색 원형에 진한 회색 v */
  :root:not([data-theme='light']) & {
    border-color: ${props => props.$checked ? '#ffffff' : 'var(--color-border)'};
    background: ${props => props.$checked ? '#ffffff' : 'var(--color-surface)'};
    color: ${props => props.$checked ? '#333333' : 'var(--color-text-gray)'};
  }
`;
